import React, { useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useIntl, defineMessages } from 'react-intl';
import { solrSearchContent } from '@kitconcept/volto-solr/actions';
import { encodeConditionTree } from '@kitconcept/volto-solr/components/theme/SolrSearch/SearchConditions';
import Select from '@fhnw/components/Form/Select/Select';
import Card from '@fhnw/components/Card/Card';
import {
  createFacetToken,
  mapToEnabledSelectOptions,
  getEnabledTokens,
  EnabledSelectOptions,
  mapTokenToLabel,
  useSearchInputFromQuery,
} from '@fhnw/components/Blocks/SolarSearch/lib';
import SearchInput from './SearchInput';
import Button from '@fhnw/components/Button/Button';
import { parseDate } from '@internationalized/date';
import { DateRangeCondition, formatDateInUTC } from './NewsView';
import { encodeExtraConditions } from './ExtraConditions';
import { useVocab } from '@fhnw/components/VocabContext/useVocab';
import { ButtonGroup } from '@designsystem/stories/03-molecules/ButtonGroup';

type ComponentViews = 'list' | 'grid';

const messages = defineMessages({
  school: {
    id: 'School',
    defaultMessage: 'School',
  },
  type: {
    id: 'Type',
    defaultMessage: 'Type',
  },
  location: {
    id: 'Location Placeholder',
    defaultMessage: 'Location',
  },
  clearFilters: {
    id: 'Clear all filters',
    defaultMessage: 'Clear all filters',
  },
  moreResults: {
    id: 'More results',
    defaultMessage: 'More results',
  },
  of: {
    id: 'of',
    defaultMessage: 'of',
  },
  list: {
    id: 'list',
    defaultMessage: 'List',
  },
  grid: {
    id: 'grid',
    defaultMessage: 'Grid',
  },
});

const EventsView = (props: any) => {
  const { locale } = useIntl();
  const intl = useIntl();
  const { data } = props;
  const batch_size = parseInt(data?.batch_size);
  const [searchInput, setSearchInput] = useSearchInputFromQuery('search_event');
  const [universitySelect, setUniversitySelect] = useState<
    EnabledSelectOptions[] | []
  >([]);
  const [eventTypeSelect, setEventTypeSelect] = useState<
    EnabledSelectOptions[] | []
  >([]);
  const [locationCategorySelect, setLocationCategorySelect] = useState<
    EnabledSelectOptions[] | []
  >([]);

  const university = useVocab('collective.taxonomy.university');
  const eventType = useVocab('collective.taxonomy.eventtype');
  const location = useVocab('collective.taxonomy.city');

  // Sync University
  const initialUniversitySelected = useMemo(() => {
    return data.universityevents?.map((token: string) =>
      mapTokenToLabel(token, university),
    );
  }, [data.universityevents, university]);

  useEffect(() => {
    if (data.universityevents?.length > 0) {
      setUniversitySelect(initialUniversitySelected);
    } else {
      setUniversitySelect([]);
    }
  }, [data.universityevents, initialUniversitySelected]);

  // Sync eventtype
  const initialEventTypeSelected = useMemo(() => {
    return data.eventtype?.map((token: string) =>
      mapTokenToLabel(token, eventType),
    );
  }, [data.eventtype, eventType]);

  useEffect(() => {
    if (data.eventtype?.length > 0) {
      setEventTypeSelect(initialEventTypeSelected);
    } else {
      setEventTypeSelect([]);
    }
  }, [data.eventtype, initialEventTypeSelected]);

  // Sync Location
  const initialLocationSelected = useMemo(() => {
    return data.city?.map((token: string) => mapTokenToLabel(token, location));
  }, [data.city, location]);

  useEffect(() => {
    if (data.city?.length > 0) {
      setLocationCategorySelect(initialLocationSelected);
    } else {
      setLocationCategorySelect([]);
    }
  }, [data.city, initialLocationSelected]);

  const defaultTemplate = data['default-template'];
  const [currentView, setCurrentView] = useState(defaultTemplate);

  React.useEffect(() => {
    setCurrentView(defaultTemplate);
  }, [defaultTemplate]);

  // const [, setCurrentView] = useState<ComponentViews>('Default');
  const dispatch = useDispatch();
  const blockuuid = props.block;
  const result = useSelector(
    (state: any) => state.solrsearch.subrequests?.[blockuuid],
  );
  // const RenderView = config.getComponent({
  //   name: 'searchBlockResultItem',
  //   dependencies: ['Event', currentView],
  // }).component;

  const enabledTokens = getEnabledTokens(result);
  const universityWithEnable: EnabledSelectOptions[] =
    mapToEnabledSelectOptions(university, 0, enabledTokens);
  const eventTypeWithEnable: EnabledSelectOptions[] = mapToEnabledSelectOptions(
    eventType,
    1,
    enabledTokens,
  );
  const locationCategoryWithEnable: EnabledSelectOptions[] =
    mapToEnabledSelectOptions(location, 2, enabledTokens);

  const onChangeSearchInput = (value: string) => {
    setSearchInput(value);
  };

  const onChangeUniversitySelect = (value: any) => {
    setUniversitySelect(value);
  };
  const onChangeEventTypeSelect = (value: any) => {
    setEventTypeSelect(value);
  };

  const onChangeLocationCategorySelect = (value: any) => {
    setLocationCategorySelect(value);
  };

  const onResetClick = () => {
    setSearchInput('');
    setLocationCategorySelect([]);
    setEventTypeSelect([]);
    setUniversitySelect([]);
  };

  const onChangeView = (view: ComponentViews) => {
    setCurrentView(view);
  };

  const onSolrSearch = (b_size: number) => {
    const facetConditions = {
      taxonomy_university: {
        c: createFacetToken(universitySelect),
        m: true,
      },
      event_type: {
        c: createFacetToken(eventTypeSelect),
        m: true,
      },
      location_category: {
        c: createFacetToken(locationCategorySelect),
        m: true,
      },
    };

    const dateRangeCondition: DateRangeCondition = {};
    dateRangeCondition.ge = formatDateInUTC(
      parseDate(new Date().toISOString().split('T')[0]),
    );
    const extra_conditions = [
      ['end', 'date-range', dateRangeCondition],
      ['Subject', 'string', { in: data.keywords || [] }],
    ];

    dispatch(
      /* @ts-ignore */
      solrSearchContent(
        '/',
        {
          SearchableText: searchInput.length < 3 ? '' : searchInput,
          group_select: 3,
          doEmptySearch: true,
          sort_on: searchInput.length < 3 ? 'end' : null,
          facet_conditions: encodeConditionTree(facetConditions),
          extra_conditions: encodeExtraConditions(extra_conditions as any),
          b_size,
          lang: locale,
        },
        blockuuid,
      ),
    );
  };

  /* eslint-disable react-hooks/exhaustive-deps */
  React.useEffect(() => {
    if (!batch_size || batch_size > result?.total) {
      onSolrSearch(result?.total);
    } else onSolrSearch(batch_size);
  }, [
    dispatch,
    searchInput,
    blockuuid,
    universitySelect,
    eventTypeSelect,
    locationCategorySelect,
    batch_size,
    result?.total,
    data.keywords,
  ]);

  const onMoreResult = () => {
    onSolrSearch(result?.items?.length + batch_size);
  };
  return (
    <div>
      <SearchInput value={searchInput} onChange={onChangeSearchInput} />
      <div className="select-container">
        {data['school-fliter'] && (
          <Select
            options={universityWithEnable}
            name="taxonomy_fieldsofstudy"
            onChange={onChangeUniversitySelect}
            isMulti={true}
            value={universitySelect}
            placeholder={intl.formatMessage(messages.school)}
          />
        )}
        {data['type-filter'] && (
          <Select
            options={eventTypeWithEnable}
            name="degree_program_types"
            onChange={onChangeEventTypeSelect}
            isMulti={true}
            value={eventTypeSelect}
            placeholder={intl.formatMessage(messages.type)}
          />
        )}
        {data['location-filter'] && (
          <Select
            options={locationCategoryWithEnable}
            name="location_category"
            onChange={onChangeLocationCategorySelect}
            isMulti={true}
            value={locationCategorySelect}
            placeholder={intl.formatMessage(messages.location)}
          />
        )}
      </div>

      <div className="reset-toggle-container">
        <Button
          label={intl.formatMessage(messages.clearFilters)}
          icon="cancel"
          type="button"
          onPress={onResetClick}
        />
        <ButtonGroup
          compact={true}
          items={[
            {
              label: intl.formatMessage(messages.list),
              type: currentView === 'list' ? 'primary' : 'secondary',
              onClick: () => onChangeView('list'),
            },
            {
              label: intl.formatMessage(messages.grid),
              type: currentView === 'grid' ? 'primary' : 'secondary',
              onClick: () => onChangeView('grid'),
            },
          ]}
        />
      </div>
      <div className="resultview-container">
        <ul
          className={
            data.template === 'grid' ? 'listing' : 'listing listing--list'
          }
        >
          {result?.items.map((item: any, index: number) => {
            if (!item) return null;
            return <Card key={index} content={item} />;
          })}
        </ul>
      </div>
      <div className="more-result-container">
        {result?.items?.length < result?.total && (
          <Button
            label={intl.formatMessage(messages.moreResults)}
            onPress={onMoreResult}
          />
        )}
        <span>{`${result?.items?.length} ${intl.formatMessage(messages.of)} ${result?.total}`}</span>
      </div>
    </div>
  );
};

export default EventsView;
